import * as React from "react";
import { ToggleContainerProps, ToggleContainerFTNodeOptions } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { UitkExpandoCard } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { getExtraClassNameBasedOnParameters } from "components/flexComponents/SimpleContainer/utilityStyles";
import { SimpleContainerFTNodeOptions } from "components/flexComponents/SimpleContainer/typings";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";
import { SimpleContainerContext, SimpleContainerToggleState } from "../SimpleContainer/SimpleContainer";

export interface ToggleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const ToggleContainerContext = React.createContext<ToggleContainerContext>(defaultContext);

const DEFAULT_WIDTH = "100";

export const ToggleContainer = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: ToggleContainerProps) => {
    const { context, blossomComponent, flexModuleModelStore, flexTemplateNode } = props;

    /* istanbul ignore next */
    if (!blossomComponent) {
      return null;
    }

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as ToggleContainerFTNodeOptions;

    const {
      containerName,
      mobileWidth,
      title,
      width,
      innerTitle,
      tabletWidth,
      desktopWidth,
      collapsed,
      hasBorder,
    } = options;

    if (!hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
    const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
    const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
    const commonClassNames = [
      "uitk-grid uitk-cell",
      getExtraClassNameBasedOnParameters(options as SimpleContainerFTNodeOptions),
    ]
      .filter((classes) => classes !== "")
      .join(" ");

    const ContainerContent = (containerProps: { includeTitle: boolean; classNames: string }) => (
      <div className={` ${commonClassNames} ${containerProps.classNames} `} role="region">
        {title && (
          <UitkSpacing>
            <UitkHeading
              tag="h2"
              size={3}
              className={`${!containerProps.includeTitle && "is-visually-hidden"} uitk-cell all-cell-1-1`}
            >
              {title}
            </UitkHeading>
          </UitkSpacing>
        )}
        {innerTitle && (
          <UitkSpacing padding={{ inlinestart: "three" }}>
            <UitkHeading tag="h3" size={3} className="uitk-cell all-cell-1-1">
              {innerTitle}
            </UitkHeading>
          </UitkSpacing>
        )}
        <BlossomComponents blossomComponent={blossomComponent} context={context} />
      </div>
    );

    const simpleContainerContext = React.useContext(SimpleContainerContext);
    const [isVisible, setIsVisible] = React.useState(!collapsed);
    const toggle = React.useCallback(() => {
      setIsVisible(!isVisible);
      simpleContainerContext.toggleCollapseAll(SimpleContainerToggleState.NEITHER);
    }, [isVisible]);

    React.useEffect(() => {
      if (simpleContainerContext.collapseAll === SimpleContainerToggleState.ALL_EXPANDED) {
        setIsVisible(true);
      } else if (simpleContainerContext.collapseAll === SimpleContainerToggleState.ALL_COLLAPSED) {
        setIsVisible(false);
      }
    }, [simpleContainerContext.collapseAll]);

    return (
      <ToggleContainerContext.Provider value={{ modulesHaveBorder: hasBorder }}>
        <Viewport>
          {mobileWidth === "hidden" ? (
            <ViewSmall />
          ) : (
            <ViewSmall>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div className="SimpleContainer ToggleContainer">
                  <UitkExpandoCard
                    isVisible={isVisible}
                    handleClick={toggle}
                    expandoTitle={title}
                    expandoDescription={
                      <ContainerContent
                        classNames={`SimpleContainer ${
                          containerName === "dx-page-header" ? " elevation-4" : ""
                        } ${gridMobileWidth}`}
                        includeTitle={false}
                      />
                    }
                  />
                </div>
              </UitkSpacing>
            </ViewSmall>
          )}

          {tabletWidth === "hidden" ? (
            <ViewMedium />
          ) : (
            <ViewMedium>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div className="SimpleContainer ToggleContainer">
                  <UitkExpandoCard
                    isVisible={isVisible}
                    handleClick={toggle}
                    expandoTitle={title}
                    expandoDescription={
                      <ContainerContent
                        classNames={`SimpleContainer ${
                          containerName === "dx-page-header" ? " elevation-4" : ""
                        } ${gridTabletWidth}`}
                        includeTitle={false}
                      />
                    }
                  />
                </div>
              </UitkSpacing>
            </ViewMedium>
          )}

          {desktopWidth === "hidden" ? (
            <ViewLarge />
          ) : (
            <ViewLarge>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div className="SimpleContainer ToggleContainer">
                  <UitkExpandoCard
                    isVisible={isVisible}
                    handleClick={toggle}
                    expandoTitle={title}
                    expandoDescription={
                      <ContainerContent
                        classNames={` ${
                          containerName === "dx-page-header" ? " elevation-4" : "SimpleContainer"
                        } ${gridWidth}`}
                        includeTitle={false}
                      />
                    }
                  />
                </div>
              </UitkSpacing>
            </ViewLarge>
          )}
        </Viewport>
      </ToggleContainerContext.Provider>
    );
  })
);

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default ToggleContainer;
